import { useCallback, useMemo, useState } from 'react';
import { compareSellerGroupPaths } from '@/lib/mise/sellerGroupTree';
import type { MiseSeller, MiseSellerGroupNode, MiseSellerNode } from '@/lib/types/mise/seller';

export type SortKey = 'name' | 'group';

export interface SortState {
  key: SortKey;
  direction: 'asc' | 'desc';
}

// 所属が近い店舗をまとめて見られるよう、初期表示は現在の店舗グループ順にする
const DEFAULT_SORT: SortState = { key: 'group', direction: 'asc' };

/**
 * 店舗-店舗グループマッピング編集の行の並び替え。
 * 店舗名順か、現在の店舗グループ（階層ツリーの並び）順で並べる。
 */
export function useSellerGroupMapSort(
  sellers: MiseSeller[],
  sellerNodes: MiseSellerNode[] | undefined,
  groupPathBySeller: Map<string, MiseSellerGroupNode[]>,
) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const sortedSellers = useMemo(() => {
    const sellerSortOrderById = new Map((sellerNodes ?? []).map((n) => [n.id, n.sort_order]));
    const compareName = (a: MiseSeller, b: MiseSeller) =>
      (a.name || a.id).localeCompare(b.name || b.id, 'ja');
    const compare = (a: MiseSeller, b: MiseSeller) => {
      if (sort.key === 'name') return compareName(a, b);
      const byGroup = compareSellerGroupPaths(
        groupPathBySeller.get(a.id) ?? [],
        groupPathBySeller.get(b.id) ?? [],
      );
      if (byGroup !== 0) return byGroup;
      // 同じグループ内は階層ツリー上の店舗の並び順に合わせる
      const orderA = sellerSortOrderById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const orderB = sellerSortOrderById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return orderA !== orderB ? orderA - orderB : compareName(a, b);
    };
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...sellers].sort((a, b) => sign * compare(a, b));
  }, [sellers, sellerNodes, groupPathBySeller, sort]);

  const handleSortChange = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  const resetSort = useCallback(() => setSort(DEFAULT_SORT), []);

  return { sort, sortedSellers, handleSortChange, resetSort };
}
